import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LogoLockup } from "@/components/Logo";
import { SITES } from "@/data/sites";
import { getReviewKey, reviewDecide, reviewList, reviewSummary, setReviewKey } from "@/lib/theories";

const TABS = ["pending", "approved", "rejected"];
const siteName = (id) => SITES.find((s) => s.id === id)?.name || id;

function Gate({ onUnlocked }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setReviewKey(key.trim());
    try {
      await reviewSummary();
      onUnlocked();
    } catch (ex) {
      setReviewKey("");
      setErr(ex.message || "Wrong passphrase");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="review-gate" onSubmit={submit} className="mx-auto mt-24 max-w-[460px] border border-[#D4AF37]/30 bg-[#12141C]/85 p-8 backdrop-blur">
      <p className="font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]">KEEPER'S REVIEW</p>
      <h1 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
        Who reads the <em className="italic text-[#D4AF37]">queue?</em>
      </h1>
      <p className="mt-4 text-sm font-light leading-relaxed text-white/60">
        Enter the review passphrase to approve or reject visitor theories before they appear on the atlas.
      </p>
      <input
        data-testid="review-key-input"
        type="password"
        autoComplete="current-password"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="Passphrase"
        className="mt-6 w-full border border-white/15 bg-[#0B0C10]/60 px-4 py-3 font-mono text-sm tracking-[0.15em] text-[#F8FAFC] placeholder:text-white/25 focus:border-[#D4AF37]/60 focus:outline-none"
      />
      {err && (
        <p data-testid="review-key-error" className="mt-3 font-mono text-[9px] tracking-[0.2em] text-[#FF9F1C]">
          {err.toUpperCase()}
        </p>
      )}
      <button
        data-testid="review-key-submit"
        disabled={!key.trim() || busy}
        className="mt-5 w-full border border-[#D4AF37] bg-[#D4AF37]/15 py-3 font-mono text-[10px] tracking-[0.3em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0B0C10] disabled:opacity-30"
      >
        {busy ? "CHECKING…" : "UNLOCK"}
      </button>
    </form>
  );
}

function Card({ t, onDecide }) {
  return (
    <li data-testid={`review-card-${t.id}`} className="border border-white/10 bg-[#12141C]/80 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-mono text-[9px] tracking-[0.3em] text-[#38BDF8]">{siteName(t.site_id).toUpperCase()}</p>
        <p className="font-mono text-[8px] tracking-[0.2em] text-white/35">
          {t.author.toUpperCase()} · {t.created_at.slice(0, 16).replace("T", " ")}
        </p>
      </div>
      <p className="mt-2 font-serif text-xl leading-snug">{t.title}</p>
      <p className="mt-2 text-[13px] font-light leading-relaxed text-white/65">{t.body}</p>
      <div className="mt-4 flex gap-2">
        {t.status !== "approved" && (
          <button
            data-testid={`review-approve-${t.id}`}
            onClick={() => onDecide(t, "approve")}
            className="border border-[#D4AF37] px-4 py-2 font-mono text-[9px] tracking-[0.25em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0B0C10]"
          >
            ✓ APPROVE
          </button>
        )}
        {t.status !== "rejected" && (
          <button
            data-testid={`review-reject-${t.id}`}
            onClick={() => onDecide(t, "reject")}
            className="border border-white/20 px-4 py-2 font-mono text-[9px] tracking-[0.25em] text-white/60 transition-colors hover:border-[#FF9F1C] hover:text-[#FF9F1C]"
          >
            × {t.status === "approved" ? "UNPUBLISH" : "REJECT"}
          </button>
        )}
      </div>
    </li>
  );
}

export default function ReviewPage() {
  const [unlocked, setUnlocked] = useState(!!getReviewKey());
  const [tab, setTab] = useState("pending");
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([reviewSummary(), reviewList(tab)]);
      setSummary(s);
      setItems(l);
    } catch (ex) {
      if (ex.status === 401 || ex.status === 429) {
        setReviewKey("");
        setUnlocked(false);
      }
      toast.error(ex.message);
    }
  }, [tab]);

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked, load]);

  const decide = async (t, decision) => {
    try {
      await reviewDecide(t.id, decision);
      toast.success(`${decision === "approve" ? "Published" : "Removed"}: ${t.title}`);
      load();
    } catch (ex) {
      toast.error(ex.message);
    }
  };

  return (
    <div data-testid="review-page" className="min-h-screen bg-[#0B0C10] px-5 pb-24 pt-6 text-[#F8FAFC] lg:px-10">
      <Toaster position="bottom-center" theme="dark" />
      <header className="flex items-center justify-between">
        <a href="/" data-testid="review-home" className="transition-opacity hover:opacity-80">
          <LogoLockup />
        </a>
        {unlocked && (
          <button
            data-testid="review-lock"
            onClick={() => {
              setReviewKey("");
              setUnlocked(false);
            }}
            className="font-mono text-[9px] tracking-[0.25em] text-white/40 transition-colors hover:text-[#FF9F1C]"
          >
            LOCK
          </button>
        )}
      </header>

      {!unlocked ? (
        <Gate onUnlocked={() => setUnlocked(true)} />
      ) : (
        <main className="mx-auto mt-14 max-w-[860px]">
          <p className="font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]">KEEPER'S REVIEW</p>
          <h1 className="mt-3 font-serif text-3xl leading-tight sm:text-5xl">
            Visitor theories, <em className="italic text-[#D4AF37]">awaiting judgement.</em>
          </h1>
          <div className="mt-8 flex flex-wrap gap-2">
            {TABS.map((k) => (
              <button
                key={k}
                data-testid={`review-tab-${k}`}
                onClick={() => setTab(k)}
                className={`border px-4 py-2 font-mono text-[9px] tracking-[0.25em] transition-colors ${
                  tab === k ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/15 text-white/50 hover:text-white"
                }`}
              >
                {k.toUpperCase()} · {summary ? summary[k] : "…"}
              </button>
            ))}
          </div>
          {items === null ? (
            <p className="mt-10 font-mono text-[9px] tracking-[0.3em] text-white/40">LOADING…</p>
          ) : items.length === 0 ? (
            <p data-testid="review-empty" className="mt-10 font-mono text-[9px] tracking-[0.3em] text-white/40">
              NOTHING {tab.toUpperCase()} — THE QUEUE IS CLEAR
            </p>
          ) : (
            <ul className="mt-8 space-y-4">
              {items.map((t) => (
                <Card key={t.id} t={t} onDecide={decide} />
              ))}
            </ul>
          )}
        </main>
      )}
    </div>
  );
}
